/* First visit: detected country -> published language. Explicit choices always win. */
(() => {
  'use strict';
  const config = document.getElementById('bk-auto-language');
  if (!config || config.dataset.designMode === 'true' || window.Shopify?.designMode) return;
  const country = config.dataset.country;
  const language = config.dataset.language;
  const languages = config.dataset.languages.split(',');
  const countries = config.dataset.countries.split(',');
  const root = config.dataset.root;
  const preferenceKey = 'bk_locale_v108';
  const guardKey = 'bk_locale_attempt';
  const defaults = { LT: 'lt', PL: 'pl', DE: 'de', AT: 'de', CH: 'de', NL: 'en', LI: 'de', FR: 'fr', MC: 'fr', ES: 'es' };
  let interacted = false;

  function read(key) {
    try {
      const item = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${key}=`));
      return item ? decodeURIComponent(item.slice(key.length + 1)) : '';
    } catch (_) { return ''; }
  }
  function write(key, value, seconds) {
    try {
      document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${seconds}; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
      return read(key) === value;
    } catch (_) { return false; }
  }
  function remember(choice) {
    return write(preferenceKey, JSON.stringify(choice), 31536000);
  }
  function preferredLanguage(code) {
    const wanted = defaults[code] || 'en';
    return languages.includes(wanted) ? wanted : (languages.includes('en') ? 'en' : language);
  }
  function setField(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.append(input);
    }
    input.value = value;
  }
  function prepare(form, choice) {
    setField(form, 'country_code', choice.country);
    setField(form, 'language_code', choice.language);
    const url = new URL(location.href);
    ['country', 'locale', 'language'].forEach((key) => url.searchParams.delete(key));
    setField(form, 'return_to', url.pathname + url.search + url.hash);
  }
  function apply(choice) {
    if (interacted || !remember(choice)) return;
    if (choice.country === country && choice.language === language) {
      write(guardKey, '', 0);
      return;
    }
    const target = `${choice.country}:${choice.language}`;
    if (read(guardKey) === target || !write(guardKey, target, 60)) return;
    const form = config.querySelector('form');
    prepare(form, choice);
    form.submit();
  }

  // Runs before the existing LocalizationForm handler submits either dropdown.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('localization-form a[data-value]');
    const form = link?.closest('localization-form').querySelector('form');
    const primary = form?.querySelector('input[name="country_code"], input[name="language_code"]');
    if (!primary) return;
    const value = link.dataset.value;
    const isCountry = primary.name === 'country_code';
    if (!(isCountry ? countries : languages).includes(value)) return;
    interacted = true;
    const choice = isCountry
      ? { country: value, language: preferredLanguage(value) }
      : { country, language: value };
    remember(choice);
    prepare(form, choice);
  }, true);

  // Keep same-store navigation in the language currently being read. Some
  // Shopify responses can contain stale locale prefixes despite an LT document.
  // Use the rendered locale root, never browser language or a previous cookie.
  const localePrefixes = languages.map(code => `/${code.toLowerCase()}`);
  function localDestination(raw) {
    if (!raw || /^(#|mailto:|tel:|javascript:)/i.test(raw)) return null;
    let url;
    try { url = new URL(raw, location.href); } catch (_) { return null; }
    if (url.origin !== location.origin) return null;
    let path = url.pathname;
    for (const prefix of localePrefixes) {
      if (path.toLowerCase() === prefix || path.toLowerCase().startsWith(`${prefix}/`)) {
        path = path.slice(prefix.length) || '/';
        break;
      }
    }
    // Leave app proxies, checkout, downloads and API endpoints untouched.
    if (!/^\/(?:$|blogs(?:\/|$)|collections(?:\/|$)|products(?:\/|$)|pages(?:\/|$)|policies(?:\/|$)|search\/?$|cart\/?$|account\/?$)/.test(path)) return null;
    const prefix = root === '/' ? '' : root.replace(/\/$/, '');
    return `${prefix}${path === '/' && prefix ? '' : path}${url.search}${url.hash}`;
  }
  function normalizeLink(element) {
    if (element.closest('localization-form') || element.hasAttribute('hreflang') || element.hasAttribute('download')) return;
    const attribute = element.tagName === 'FORM' ? 'action' : 'href';
    if (attribute === 'action' && (element.method.toLowerCase() !== 'get' || !/\/search\/?(?:[?#]|$)/.test(element.getAttribute(attribute) || ''))) return;
    const raw = element.getAttribute(attribute);
    const next = localDestination(raw);
    if (next !== null && raw !== next) element.setAttribute(attribute, next);
  }
  function normalizeTree(node) {
    if (!(node instanceof Element)) return;
    if (node.matches('a[href],form[action]')) normalizeLink(node);
    node.querySelectorAll('a[href],form[action]').forEach(normalizeLink);
  }
  normalizeTree(document.body);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes') normalizeLink(record.target);
      else record.addedNodes.forEach(normalizeTree);
    }
  }).observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['href', 'action']});
  // Guard newly inserted links before their first click, including modified clicks.
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (link) normalizeLink(link);
  }, true);

  // Reading a content page or opening a localized URL establishes the current
  // language. Geo detection is only for a fresh visit to the home page.
  if (location.pathname !== '/') {
    remember({ country, language });
    write(guardKey, '', 0);
    return;
  }
  // Honor an explicit country link, e.g. an ad with ?country=PL.
  const explicitCountry = new URL(location.href).searchParams.get('country');
  if (countries.includes(explicitCountry)) {
    apply({ country: explicitCountry, language: preferredLanguage(explicitCountry) });
    return;
  }
  let saved;
  try { saved = JSON.parse(read(preferenceKey)); } catch (_) { /* First visit. */ }
  if (saved && countries.includes(saved.country) && languages.includes(saved.language)) {
    apply(saved);
    return;
  }
  // The dedicated Polish domain is an explicit market destination.
  // Keep manual choices above, but do not GeoIP-switch first-time visitors away.
  if (location.hostname.replace(/^www\./, '') === 'bobrowekosmetyki.pl') {
    apply({ country: countries.includes('PL') ? 'PL' : country, language: preferredLanguage('PL') });
    return;
  }
  // A dedicated German storefront is also an explicit destination. Preserve
  // manual choices above and the selected DE/AT/CH market; never GeoIP-switch
  // a fresh root visitor to the Lithuanian storefront.
  if (location.hostname.replace(/^www\./, '') === 'biberkosmetik.de') {
    const germanCountry = ['DE', 'AT', 'CH', 'NL'].includes(country) ? country :
      (countries.includes('DE') ? 'DE' : country);
    apply({ country: germanCountry, language: preferredLanguage('DE') });
    return;
  }
  // Each dedicated domain has its native language as the fresh default.
  // Keep the selected market and manual preferences; visitors choose any
  // other language themselves instead of being GeoIP-routed to /en.
  if (location.hostname.replace(/^www\./, '') === 'bebrukosmetika.lt') {
    apply({ country, language: preferredLanguage(country === 'NL' ? 'NL' : 'LT') });
    return;
  }

  if (read(guardKey) || !write(guardKey, 'lookup', 60)) return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2500);
  fetch(`${root.replace(/\/$/, '')}/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false`, {
    credentials: 'same-origin', cache: 'no-store', signal: controller.signal
  }).then((response) => {
    if (!response.ok) throw new Error('Localization lookup unavailable');
    return response.json();
  }).then((data) => {
    const detected = data?.detected_values?.country?.handle;
    if (typeof detected !== 'string' || !/^[A-Z]{2}$/.test(detected)) return;
    apply({
      country: countries.includes(detected) ? detected : country,
      language: preferredLanguage(detected)
    });
  }).catch(() => { /* Keep the storefront usable; its dropdowns still work. */ })
    .finally(() => clearTimeout(timeout));
})();
